import { AuditLog } from 'api/Clients/Types/AuditLog'
import { NameDto } from 'api/Common/Types'
import { PageData } from 'api/Pagination/Types'
import { CreateHighRiskClientTicket } from 'api/Tickets/Types/CreateHighRiskClientTicket'
import { OutstandingUserTicketsSummaryDto } from 'api/Tickets/Types/OutstandingUserTicketsSummaryDto'
import { TicketDetailsDto } from 'api/Tickets/Types/TicketDetails'
import { TicketDto } from 'api/Tickets/Types/TicketDto'
import { TicketStatus } from 'api/Tickets/Types/TicketStatus'
import { TicketType } from 'api/Tickets/Types/TicketType'
import { UpdateTicket } from 'api/Tickets/Types/UpdateTicket'
import { TicketsViewType } from 'routes/Tickets/TicketsViewType'

import { ApiClient } from './ApiClient'
import { PageQuery, preparePageQueryParams } from './index'

export class TicketsApiClient {
  constructor(private apiClient: ApiClient) {}

  getTickets: (
    viewType: TicketsViewType | null,
    query?: PageQuery
  ) => Promise<PageData<TicketDto[]>> = (viewType, query) => {
    let url = `tickets/tickets`
    if (viewType === TicketsViewType.Payments) {
      url += '/payments'
    }
    if (viewType === TicketsViewType.BackOffice) {
      url += '/backoffice'
    }
    if (viewType === TicketsViewType.Compliance) {
      url += '/compliance'
    }
    if (viewType === TicketsViewType.Translation) {
      url += '/translations'
    }
    if (viewType === TicketsViewType.Brokerage) {
      url += '/brokerage'
    }
    if (viewType === TicketsViewType.Sales) {
      url += '/sales'
    }
    if (viewType === TicketsViewType.MyTickets) {
      url += '/my-tickets'
    }
    if (viewType === TicketsViewType.All) {
      url += ''
    }
    return this.apiClient.get(`${url}?${preparePageQueryParams(query)}`)
  }
  getTicketDetails: (id: string) => Promise<TicketDetailsDto> = (id) => {
    return this.apiClient.get(`tickets/tickets/${id}`)
  }
  updateTicket: (data: UpdateTicket) => Promise<void> = (data) => {
    return this.apiClient.put(`tickets/tickets/${data.id}`, data)
  }
  addTicketTranslationTypeAssignee: (documentId: string, assigneeId: string) => Promise<void> = (
    documentId,
    assigneeId
  ) => {
    return this.apiClient.post(`tickets/tickets/${documentId}/translation`, { assigneeId })
  }
  completeTickets: (ticketIds: string[], viewTypeId: TicketsViewType) => Promise<void> = (
    ticketIds,
    viewTypeId
  ) => {
    return this.apiClient.put(`tickets/tickets/complete`, { ticketIds, viewTypeId })
  }
  completeClientTickets: (ticketIds: string[], viewTypeId: TicketsViewType) => Promise<void> = (
    ticketIds,
    viewTypeId
  ) => {
    return this.apiClient.put(`tickets/tickets/complete-client-tickets`, { ticketIds, viewTypeId })
  }
  acquireTickets: ({
    ticketIds,
    viewTypeId,
    assigneeId,
  }: {
    ticketIds: string[]
    viewTypeId: TicketsViewType
    assigneeId?: string
  }) => Promise<void> = ({ ticketIds, viewTypeId, assigneeId }) => {
    return this.apiClient.put(`tickets/tickets/acquire`, { ticketIds, assigneeId, viewTypeId })
  }
  acquireClientTickets: ({
    ticketIds,
    viewTypeId,
    assigneeId,
  }: {
    ticketIds: string[]
    viewTypeId: TicketsViewType
    assigneeId?: string
  }) => Promise<void> = ({ ticketIds, viewTypeId, assigneeId }) => {
    return this.apiClient.put(`tickets/tickets/acquire-client-tickets`, {
      ticketIds,
      assigneeId,
      viewTypeId,
    })
  }
  assigneeClientToTickets: ({
    assigneeId,
    ticketIds,
  }: {
    assigneeId?: string
    ticketIds: string[]
  }) => Promise<void> = ({ assigneeId, ticketIds }) => {
    return this.apiClient.put(`tickets/tickets/assign`, { ticketIds, assigneeId })
  }
  processPaymentTickets: ({ transactionIds }: { transactionIds: string[] }) => Promise<void> = ({
    transactionIds,
  }) => {
    return this.apiClient.post(`tickets/tickets/transactions-bulk-process`, {
      transactionList: transactionIds,
    })
  }
  getTicketAuditLogs: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`tickets/tickets/${id}/events`)
  }
  getTicketStatuses: () => Promise<TicketStatus[]> = () => {
    return this.apiClient.get(`tickets/tickets/statuses`)
  }
  getMlStatuses: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`tickets/tickets/ml-statuses`)
  }
  getTicketTypes: (viewType: number) => Promise<TicketType[]> = (viewType) => {
    if (viewType === TicketsViewType.MyTickets) {
      return this.apiClient.get(`tickets/tickets/types`)
    }
    return this.apiClient.get(`tickets/tickets/view-types/${viewType}/types`)
  }
  getTicketsAssignedToUserSession: () => Promise<OutstandingUserTicketsSummaryDto[]> = () => {
    return this.apiClient.get(`tickets/tickets/me/outstanding`)
  }
  addTicketHighRiskClients: (data: CreateHighRiskClientTicket) => Promise<void> = (data) => {
    return this.apiClient.post(`tickets/tickets/high-risk-clients`, data)
  }

  getMyTicketViewTypes: () => Promise<TicketType[]> = () => {
    return this.apiClient.get(`tickets/tickets/my-tickets-view-types`)
  }
}
